import { Divider, PrintView } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ForwardedRef, forwardRef, Fragment, useMemo } from 'react';
import { isEqual, last } from 'lodash';
import AnalystsWidget from '../../../components/widgets/Analysts';
import { PageComponentsMap } from '../../../models';
import { IssuerDto } from '../../../services/dtos/issuer/issuer.dto';
import CompanyHeader from '../CompanyHeader';
import { ORDERED_PRINTABLE_SECTIONS } from '../CompanyPrintAction/CompanyPrintAction.constants';
import CompanyOverviewPrintViewSection from './CompanyOverviewPrintViewSection';
import styles from './CompanyOverviewPrintView.module.scss';

const cx = classNames.bind(styles);

interface CompanyOverviewPrintViewProps {
  selectedSections: string[];
  pageComponentsMap: PageComponentsMap;
  company: IssuerDto;
  userFullName?: string;
  disclaimer?: string;
  redistributionAllowed?: boolean;
}

const CompanyOverviewPrintView = (
  {
    selectedSections,
    pageComponentsMap,
    company,
    userFullName,
    disclaimer,
    redistributionAllowed,
  }: CompanyOverviewPrintViewProps,
  ref: ForwardedRef<HTMLElement>
): React.ReactElement => {
  const orderedSections = useMemo(
    () => ORDERED_PRINTABLE_SECTIONS.filter((section) => selectedSections.includes(section)),
    [selectedSections]
  );
  return (
    <PrintView
      ref={ref}
      isHeaderFooterVisible
      userFullName={userFullName}
      disclaimer={disclaimer}
      redistributionAllowed={redistributionAllowed}
    >
      <div className={cx('body')}>
        <CompanyHeader name={company.displayName} ticker={company.displayTicker} />
        <AnalystsWidget analysts={company.analysts} print />
        <Divider />
        {orderedSections.map((section) => (
          <Fragment key={section}>
            <CompanyOverviewPrintViewSection
              section={section}
              pageComponentsMap={pageComponentsMap}
              company={company}
            />
            {!isEqual(last(selectedSections), section) && <Divider />}
          </Fragment>
        ))}
      </div>
    </PrintView>
  );
};

export default forwardRef(CompanyOverviewPrintView);
